import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { AnalyticsQueryFilter } from '../util/analytics-query-filter';
import { FETCH_LOCATION_ANALYTICS_QUERY_KEY } from './analytics-query-keys';

const PAGE_SIZE = 100000;

export type FetchLocationAnalyticsResult = {
    data: { coordinates: number[][] } | undefined;
    status: 'error' | 'success' | 'pending';
    refetch: () => void;
    /** How much of the point set has arrived, 0-100. */
    progress: number;
};

/**
 * Every location point behind the heatmap.
 *
 * A busy company can have hundreds of thousands of points, so the endpoint is
 * cursor-paged and this read follows the cursor to the end before resolving —
 * a partial heatmap would be misleading rather than merely incomplete. The
 * pages arrive slowly enough to be worth reporting on, hence `progress`.
 */
export function useFetchLocationAnalytics(
    query: AnalyticsQueryFilter
): FetchLocationAnalyticsResult {
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const [progress, setProgress] = useState<number>(0);
    const { status, data, refetch } = useQuery({
        queryKey: [FETCH_LOCATION_ANALYTICS_QUERY_KEY, query],
        queryFn: () => {
            let totalCoordinates: number | undefined = undefined;
            let coordinatesDownloaded = 0;

            function fetchNextPage(cursor?: string): Promise<number[][]> {
                return openApiRef.current.analytics
                    .getLocations({ ...query, cursor, limit: PAGE_SIZE })
                    .then((response) => {
                        const coordinates = response.data.data.coordinates;
                        const nextCursor = response.data.data.nextCursor;

                        coordinatesDownloaded += coordinates.length;

                        if (response.data.data.totalCoordinates) {
                            totalCoordinates =
                                response.data.data.totalCoordinates;
                        }

                        if (totalCoordinates) {
                            setProgress(
                                Math.round(
                                    (coordinatesDownloaded / totalCoordinates) *
                                        100
                                )
                            );
                        }

                        if (!isMountedRef.current) {
                            throw new Error(
                                'Component unmounted while fetching data'
                            );
                        }

                        if (nextCursor) {
                            return fetchNextPage(nextCursor).then(
                                (nextCoordinates) => {
                                    return coordinates.concat(nextCoordinates);
                                }
                            );
                        }
                        return coordinates;
                    });
            }

            setProgress(0);
            return fetchNextPage().then((coordinates) => {
                return { coordinates };
            });
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 0
    });

    return {
        status,
        data,
        progress,
        refetch: () => {
            void refetch();
        }
    };
}
